import { localOf } from "../home/liveNowPicks.js";

/**
 * "On the guide" results, one per show (owner, 2026-10-13: "search shows too many local channels
 * for the same show. Only show the local for that profile if it's playing there, use next local.
 * Big Bang Theory showed 40 channels, no need to click past all of them").
 *
 * A syndicated show is on dozens of local stations at once. The viewer wants one row: the
 * station that is theirs, if it has the show on; otherwise the best other place it is on.
 *
 *   0  the viewer's own local station, on now
 *   1  any other channel (a cable network, a national feed), on now
 *   2  another city's local station, on now (when the viewer's own does not have it on, the next
 *      one that does: "if it isn't playing on the local affiliate pick the next one")
 *   3  the viewer's own local station, later
 *   4  any other channel, later
 *   5  another city's local station, later
 *
 * Programmes that have already ended are dropped: tuning in would not show them. Pure, so it is
 * tested without a screen.
 */

export interface AiringCandidate {
  /** The channel's name, which says whether it is a local station and of which city. */
  channelName: string;
  channelNumber?: number | undefined;
  /** Epoch ms. */
  start: number;
  end: number;
}

/** Lower is better. */
export function airingTier(candidate: AiringCandidate, now: number, market: string | null): number {
  const onNow = candidate.start <= now && now < candidate.end;
  const local = localOf(candidate.channelName);
  // With no market known, no station can be told apart as "theirs": all are treated alike.
  const otherCity = local !== undefined && market !== null && local.market !== market;
  const own = local !== undefined && market !== null && local.market === market;
  if (otherCity) return onNow ? 2 : 5;
  if (own) return onNow ? 0 : 3;
  return onNow ? 1 : 4;
}

/** The index of the airing to show, or -1 when every one has already ended. */
export function bestAiring(candidates: readonly AiringCandidate[], now: number, market: string | null): number {
  let best = -1;
  let bestKey: [number, number, number, string] | null = null;
  candidates.forEach((candidate, index) => {
    if (candidate.end <= now) return;
    const key: [number, number, number, string] = [
      airingTier(candidate, now, market),
      // Sooner first: on now ties go to the one that started most recently only by name below;
      // later airings go to the soonest.
      Math.max(0, candidate.start - now),
      candidate.channelNumber ?? Number.MAX_SAFE_INTEGER,
      candidate.channelName.toLowerCase(),
    ];
    if (
      bestKey === null ||
      key[0] < bestKey[0] ||
      (key[0] === bestKey[0] &&
        (key[1] < bestKey[1] ||
          (key[1] === bestKey[1] && (key[2] < bestKey[2] || (key[2] === bestKey[2] && key[3] < bestKey[3])))))
    ) {
      best = index;
      bestKey = key;
    }
  });
  return best;
}
